import { useMemo, useState, useCallback } from "react";
import type { LayoutChangeEvent } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { MediaCarousel, type MediaCarouselItem } from "@visioni/a3s-ui";
import { Card, Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { IconButton } from "@src/components/IconButton";
import { ClipVideoPlayer, downloadVideo, handleDownloadError } from "@src/features/clips";
import type { Clip } from "@src/features/clips";
import { useClips, type ClipItem } from "@visioni/a3s-media/app";
import { toISODateString } from "@visioni/a3s-codecs";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import type { WasteSenseDumpDetection } from "@visioni/a3s-reports/client-messages";
import type { DumpDetectionsView, DumpMetaPosition } from "./WasteSenseControls";

interface DumpDetectionsProps {
  dumps: readonly WasteSenseDumpDetection[];
  /**
   * How to present the dumps. `list` (default) is the stacked-card layout;
   * `carousel` pages through one dump at a time via the shared MediaCarousel.
   */
  view?: DumpDetectionsView;
  /**
   * Where the dump metadata sits in the carousel (`below-clip` default,
   * `bottom`, or `top`). Only meaningful when `view === "carousel"`; the list
   * layout ignores it.
   */
  metaPosition?: DumpMetaPosition;
}

/**
 * "Dump Detections" section — operator-confirmed dumps, each with its evidence
 * clip. Rendered FIRST in the report (before the weekly summary) so the most
 * important information leads.
 *
 * Clip playback reuses the media `useClips` → `ClipVideoPlayer` path (mirroring
 * `DumpingSearch.web.tsx`): the payload carries clip *identifiers*
 * (`site_id` + `asset_camera_key` + `clip_timestamp`), and each card resolves a
 * short-lived presigned URL at view time. We deliberately do NOT bake a URL
 * into the stored report — presigned URLs expire.
 */
export function DumpDetections({ dumps, view = "list", metaPosition = "below-clip" }: DumpDetectionsProps) {
  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Dump Detections
      </Text>

      {dumps.length === 0 ?
        <Card px="md" py="xl" alignItems="center">
          <Text textStyle="sm/regular" color="textMuted">
            No dumps detected
          </Text>
        </Card>
      : view === "carousel" ?
        <DumpCarousel dumps={dumps} metaPosition={metaPosition} />
      : <View gap="md">
          {dumps.map((dump) => (
            <DumpCard key={dump.dump_id} dump={dump} />
          ))}
        </View>
      }
    </View>
  );
}

/**
 * Adapt a media-service {@link ClipItem} to the clips feature's {@link Clip} so
 * the report can reuse {@link ClipVideoPlayer}. Mirrors the adapter in
 * `DumpingSearch.web.tsx`: `video.url` is the canonical source, `videoUrl` the
 * deprecated fallback; the clip's start time doubles as its id/timestamp.
 */
function clipItemToClip(item: ClipItem): Clip {
  return {
    id: item.startTime,
    timestamp: item.startTime,
    thumbnailUrl: item.thumbnailUrl ?? null,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- fallback for older deployed apps
    videoUrl: item.video?.url ?? item.videoUrl ?? "",
    duration: item.durationMs,
  };
}

/**
 * Resolves a dump's evidence clip and exposes a download handler. Shared by the
 * list card and the carousel slide so both present the same clip and download
 * behaviour. Runs `useClips` for the single confirmed-dump clip (a zero-width
 * `[t, t]` range query) — so when this hook lives inside a carousel slide, only
 * the mounted (active + preloaded) slots ever fetch.
 */
function useDumpClip(dump: WasteSenseDumpDetection) {
  const parsedSiteId = useMemo(() => SiteId.fromString(dump.site_id), [dump.site_id]);
  const parsedCameraKey = useMemo(() => AssetCameraKey.fromString(dump.asset_camera_key), [dump.asset_camera_key]);
  // The clip identity is its exact start time. `useClips` range-queries with an
  // inclusive `between`, so a zero-width [t, t] window returns the single clip
  // whose startTime === clip_timestamp (the confirmed-dump clip).
  const normalizedTimestamp = useMemo(() => toISODateString(new Date(dump.clip_timestamp)), [dump.clip_timestamp]);

  const {
    data: clips,
    isPending,
    isError,
  } = useClips({
    siteId: parsedSiteId,
    assetCameraKey: parsedCameraKey,
    startTime: normalizedTimestamp,
    endTime: normalizedTimestamp,
  });

  // Match the exact clip by start time; fall back to the first returned clip in
  // case of sub-millisecond rounding differences in the range query.
  const playerClip = useMemo(() => {
    if (!clips || clips.length === 0) return null;
    const exact = clips.find((c) => c.startTime === dump.clip_timestamp) ?? clips[0];
    return exact ? clipItemToClip(exact) : null;
  }, [clips, dump.clip_timestamp]);

  // Download the evidence clip. Mirrors `useClipsLogic.handleDownload`: on web
  // `downloadVideo` opens the (short-lived, resolved-at-view-time) presigned URL
  // in a new tab and toasts; failures route through `handleDownloadError`. The
  // `isDownloading` guard both disables the button and blocks re-entrancy while
  // a download is in flight.
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = useCallback(async () => {
    if (!playerClip?.videoUrl || isDownloading) return;
    try {
      setIsDownloading(true);
      await downloadVideo({
        videoUrl: playerClip.videoUrl,
        timestamp: new Date(dump.clip_timestamp),
      });
    } catch (err: unknown) {
      handleDownloadError(err);
    } finally {
      setIsDownloading(false);
    }
  }, [playerClip?.videoUrl, isDownloading, dump.clip_timestamp]);

  return { playerClip, isPending, isError, isDownloading, handleDownload };
}

interface DumpCardProps {
  dump: WasteSenseDumpDetection;
}

// Bounded width for the clip column on wide viewports. We cap the column so a
// full-width card doesn't stretch the clip box arbitrarily wide. The clip box
// is given an explicit width×height (via ClipVideoPlayer's `boxHeight`) and the
// video is `contain`-fit inside it — the WHOLE frame always shows, with black
// bars filling any aspect-ratio mismatch. Nothing is ever cropped.
const CLIP_COLUMN_WIDTH = 400;
// Fixed clip-box height for the side-by-side layout. 400×225 is 16:9, which
// matches the production Axis cameras — the box is shaped for the clips we
// actually expect, so there are no wasted bars in prod. The int test clips are
// 4:3 (a testing artefact) and simply pillarbox (black bars left/right) inside
// the 16:9 box — still fully visible, never cropped.
const CLIP_BOX_HEIGHT = (CLIP_COLUMN_WIDTH * 9) / 16;
// Card horizontal padding — must track the `px="md"` on the <Card> below so the
// stacked clip's width math subtracts the right inset (spacing token md = 8px).
const CARD_PADDING_X = 8;
// Below this *card* width we stack (clip on top, meta below) rather than sit
// side-by-side: the clip needs CLIP_COLUMN_WIDTH and the metadata needs enough
// room left over to stay legible (labels like "DESCRIPTION" must not wrap
// mid-word). Measured on the card itself — NOT the window — so it reacts to the
// real available space (sidebar open/closed, split panes), not the raw viewport.
const SIDE_BY_SIDE_MIN_WIDTH = CLIP_COLUMN_WIDTH + 350;
// Glyph size (px) for the download icon. A literal size, not a spacing token —
// icon dimensions are a glyph/typographic concern, and this mirrors the sibling
// `CameraSnapshot` (SIZE_PLAY_ICON = 14). Sits comfortably in the 32px "sm"
// IconButton.
const DOWNLOAD_ICON_SIZE = 18;

function DumpCard({ dump }: DumpCardProps) {
  // Measure the card's own width so the row→column flip responds to the actual
  // content area, not the browser window (the report sits beside a sidebar, so
  // window width overstates the room a card really has).
  const [cardWidth, setCardWidth] = useState<number | null>(null);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setCardWidth(e.nativeEvent.layout.width);
  }, []);
  // Until first measure, assume side-by-side (the common desktop case) to avoid
  // a stacked→row flash on wide screens.
  const sideBySide = cardWidth === null || cardWidth >= SIDE_BY_SIDE_MIN_WIDTH;

  const { playerClip, isPending, isError, isDownloading, handleDownload } = useDumpClip(dump);

  const { theme } = useUnistyles();

  return (
    <Card px="md" py="md" onLayout={onLayout}>
      <View flexDirection={sideBySide ? "row" : "column"} gap="md" alignItems={sideBySide ? "flex-start" : "stretch"}>
        {/* Evidence clip — LEFT on wide viewports, on TOP when stacked. Bounded
            width so the whole frame shows (contentFit=contain, never cropped).
            Paused on the first frame so a scan holds still; the reviewer hits
            play to inspect the dump in motion. */}
        <View style={sideBySide ? { width: CLIP_COLUMN_WIDTH, flexShrink: 0 } : { width: "100%" }}>
          {
            playerClip ?
              <ClipVideoPlayer
                key={playerClip.id}
                clip={playerClip}
                autoPlay={false}
                // Explicit width×height box with contain-fit video (whole frame
                // always shown, black bars for any aspect mismatch — never
                // cropped). screenWidth/screenHeight are unused for sizing in
                // boxHeight mode but remain required props, so pass sensible
                // values. Side-by-side: fixed 400×225 (16:9, matches the Axis
                // cameras). Stacked: the card is full width, so scale the box
                // height to keep the same 16:9 shape (width × 9/16).
                screenWidth={sideBySide ? CLIP_COLUMN_WIDTH : (cardWidth ?? CLIP_COLUMN_WIDTH) - CARD_PADDING_X * 2}
                screenHeight={CLIP_COLUMN_WIDTH * 3}
                boxHeight={
                  sideBySide ? CLIP_BOX_HEIGHT : (((cardWidth ?? CLIP_COLUMN_WIDTH) - CARD_PADDING_X * 2) * 9) / 16
                }
                selectedHour={0}
              />
              // Only show "Loading" while the query is genuinely in flight. Once
              // it has settled without a clip — empty result (clip deleted / bad
              // key) or an API error — show "unavailable" instead of spinning.
            : isPending ?
              <View py="xl" alignItems="center">
                <Text textStyle="xs/regular" color="textMuted">
                  Loading clip…
                </Text>
              </View>
            : <View py="xl" alignItems="center">
                <Text textStyle="xs/regular" color="textMuted">
                  {isError ? "Clip unavailable" : "No clip found"}
                </Text>
              </View>

          }
        </View>

        {/* Metadata — RIGHT of the clip on wide viewports, below when stacked. */}
        <View flex={sideBySide ? 1 : undefined} gap="sm" style={sideBySide ? { minWidth: 0 } : undefined}>
          {/* Header row: meta fields on the left, a top-right download control.
              The button only appears once a clip has resolved — there is nothing
              to download otherwise (loading / missing / errored clip). */}
          <View flexDirection="row" gap="md" justifyContent="space-between" alignItems="flex-start">
            <View gap="sm" style={{ minWidth: 0 }}>
              {/* Site is shown so the leading list is legible on multi-site
                  reports — each payload row carries site_name; a single-site
                  report just repeats the one site, which is harmless. */}
              <Meta label="Site" value={dump.site_name} />
              <Meta label="Detected" value={dump.detected_at_label} />
              <Meta label="Object" value={dump.object_label} />
            </View>

            {playerClip ?
              <IconButton
                size="sm"
                title="Download clip"
                onPress={handleDownload}
                disabled={isDownloading}
                Icon={() => <Icon name="download" size={DOWNLOAD_ICON_SIZE} color={theme.colors.text} />}
              />
            : null}
          </View>

          {dump.description !== "" ?
            <View gap="xxs">
              <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
                Description
              </Text>
              <Text textStyle="sm/regular" color="text">
                {dump.description}
              </Text>
            </View>
          : null}
        </View>
      </View>
    </Card>
  );
}

interface MetaProps {
  label: string;
  value: string;
}

// Media area shape for the carousel. 16:9 matches the production Axis cameras;
// non-16:9 clips (e.g. 4:3 int test clips) contain-fit inside with black bars —
// never cropped, consistent with the list view.
const CAROUSEL_MEDIA_ASPECT_RATIO = 16 / 9;
// Fixed media-area height for the carousel. The surface spans the FULL carousel
// width (mediaFillWidth) at this height, so it lines up edge-to-edge with the
// thumbnail strip below rather than floating as a narrower centred box. The clip
// contain-fits inside (whole frame shown; black bars for any aspect mismatch),
// so a non-16:9 clip letterboxes within the full-width surface instead of the
// surface itself pillarboxing on the page. 450px keeps the clip prominent
// without pushing the meta/description below the fold on a typical report width.
const CAROUSEL_MEDIA_HEIGHT = 450;
// Thumbnail strip dimensions (16:9 to match the media). Small enough to fit a
// row of dumps without dominating the section.
const CAROUSEL_THUMBNAIL_WIDTH = 96;
// Below this detail-area width the meta row can't hold Object + Detected + Site
// + download comfortably (Site is double-width and its name is often long — it
// gets squeezed against the download icon and "Detected"/"Object" wrap
// mid-value). So under this width Site drops to its own full-width second row.
// 650px is tuned so the flip happens BEFORE the fields start wrapping (a 3-cell
// row plus a long site name needs the headroom). Measured on the detail area
// itself (not the window) so it tracks the real space — the report sits beside
// a sidebar, so window width overstates the room available.
const META_SITE_WRAP_MIN_WIDTH = 650;

/** One carousel slide's data — a dump plus the id the carousel keys slots on. */
interface DumpCarouselItem extends MediaCarouselItem {
  readonly dump: WasteSenseDumpDetection;
}

/**
 * Map the report's meta-position control to the carousel's two placement props.
 * The meta lives in the carousel's detail slot; the clip is the media and the
 * jump strip is the thumbnails. The three options are:
 *   - below-clip: clip → meta → thumbnails  (detail below media, strip last)
 *   - bottom:     clip → thumbnails → meta  (detail below media, strip between)
 *   - top:        meta → clip → thumbnails  (detail above media, strip at bottom)
 */
function metaPositionToPlacements(metaPosition: DumpMetaPosition): {
  detailPlacement: "above-media" | "below-media";
  thumbnailPlacement: "top" | "bottom" | "below-detail";
} {
  switch (metaPosition) {
    case "bottom":
      return { detailPlacement: "below-media", thumbnailPlacement: "bottom" };
    case "top":
      return { detailPlacement: "above-media", thumbnailPlacement: "bottom" };
    case "below-clip":
    default:
      return { detailPlacement: "below-media", thumbnailPlacement: "below-detail" };
  }
}

/**
 * Carousel presentation of the dumps: one dump at a time, clip on top and its
 * meta below, with a thumbnail strip to jump between dumps. The meta area is
 * driven by the carousel's `renderDetail` (called with the ACTIVE item), so it
 * always reflects the selected clip — we supply the content, the carousel owns
 * the selection/sync.
 */
function DumpCarousel({
  dumps,
  metaPosition,
}: {
  dumps: readonly WasteSenseDumpDetection[];
  metaPosition: DumpMetaPosition;
}) {
  const items = useMemo<DumpCarouselItem[]>(
    () =>
      dumps.map((dump) => ({
        id: dump.dump_id,
        dump,
        aspectRatio: CAROUSEL_MEDIA_ASPECT_RATIO,
        accessibilityLabel: `${dump.site_name} — ${dump.object_label}`,
      })),
    [dumps],
  );

  const { detailPlacement, thumbnailPlacement } = metaPositionToPlacements(metaPosition);

  return (
    <MediaCarousel<DumpCarouselItem>
      items={items}
      mediaAspectRatio={CAROUSEL_MEDIA_ASPECT_RATIO}
      mediaHeight={CAROUSEL_MEDIA_HEIGHT}
      mediaFillWidth
      showThumbnails
      thumbnailWidth={CAROUSEL_THUMBNAIL_WIDTH}
      thumbnailPlacement={thumbnailPlacement}
      detailPlacement={detailPlacement}
      accessibilityLabel="Detected dumps"
      renderMedia={({ item, isActive }) => <DumpSlideMedia dump={item.dump} isActive={isActive} />}
      renderDetail={({ item }) => <DumpSlideDetail dump={item.dump} placement={detailPlacement} />}
      renderThumbnail={({ item }) => <DumpThumbnail dump={item.dump} />}
    />
  );
}

/**
 * A single carousel slide's clip. Resolves its own clip via `useDumpClip`;
 * because the carousel only mounts the active item plus its preloaded
 * neighbours, only those slots ever fetch. The player is `autoPlay={false}`
 * (paused first frame) — and when this slot is a preloaded neighbour
 * (`isActive` false) it's hidden and inert anyway, so nothing plays off-screen.
 */
function DumpSlideMedia({ dump, isActive }: { dump: WasteSenseDumpDetection; isActive: boolean }) {
  const { playerClip, isPending, isError } = useDumpClip(dump);

  if (playerClip) {
    return (
      <ClipVideoPlayer
        key={playerClip.id}
        clip={playerClip}
        // Never autoplay in the report; a paused first frame holds still until
        // the reviewer hits play. Preloaded (inactive) slots are hidden+inert.
        autoPlay={false}
        // Pause when this slide isn't the active one. The carousel keeps
        // inactive slides mounted (hidden) for instant paging and preserved
        // buffers, so without this a clip the reviewer started would keep
        // playing/audible off-screen after they page away.
        paused={!isActive}
        // Fill the carousel's media surface (already shaped 16:9, bounded and
        // centred). fillParent = the player takes 100%×100% of that box and
        // contain-fits the video — so it tracks the surface at any width
        // instead of a fixed height that would mismatch (and crop) when the
        // surface is shorter than the max on narrower layouts.
        fillParent
        // screenWidth/screenHeight are unused for sizing in fillParent mode but
        // remain required props; pass the max-case dimensions as sane values.
        screenWidth={CAROUSEL_MEDIA_HEIGHT * CAROUSEL_MEDIA_ASPECT_RATIO}
        screenHeight={CAROUSEL_MEDIA_HEIGHT}
        selectedHour={0}
      />
    );
  }

  return (
    <View flex={1} alignItems="center" justifyContent="center" py="xl">
      <Text textStyle="xs/regular" color="textMuted">
        {isPending ?
          "Loading clip…"
        : isError ?
          "Clip unavailable"
        : "No clip found"}
      </Text>
    </View>
  );
}

/**
 * The meta area beneath the active carousel slide — mirrors the list card's
 * metadata (Site / Detected / Object / Description) plus the download button.
 * Resolves the clip again via `useDumpClip` so the download targets the active
 * dump's clip; the query is shared/cached with the media slot for the same
 * dump, so this doesn't double-fetch.
 */
function DumpSlideDetail({
  dump,
  placement,
}: {
  dump: WasteSenseDumpDetection;
  placement: "above-media" | "below-media";
}) {
  const { playerClip, isDownloading, handleDownload } = useDumpClip(dump);
  const { theme } = useUnistyles();

  // Measure the detail area's own width so the Site→second-row flip responds to
  // the actual content area, not the browser window (the report sits beside a
  // sidebar, so window width overstates the room a slide really has).
  const [detailWidth, setDetailWidth] = useState<number | null>(null);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setDetailWidth(e.nativeEvent.layout.width);
  }, []);
  // Until first measure, assume the wide single-row layout (the common desktop
  // case) to avoid a two-row→one-row flash on wide screens.
  const siteInline = detailWidth === null || detailWidth >= META_SITE_WRAP_MIN_WIDTH;

  return (
    // The gap goes on the side facing the clip: when the meta sits BELOW the
    // clip (below-media) that's a top margin; when it sits ABOVE the clip
    // (above-media, the "Top" option) the same gap belongs on the bottom, or
    // the meta crowds the clip's top edge (a plain `mt` would push it away from
    // the report header instead, leaving it stuck to the clip).
    <View
      gap="sm"
      mt={placement === "below-media" ? "md" : undefined}
      mb={placement === "above-media" ? "md" : undefined}
      onLayout={onLayout}
    >
      {/* Line 1: meta fields — Object, Detected, and (when wide) Site, with the
          download control pinned to the right. `minWidth: 0` on each cell lets
          long values ellipsize/wrap within their share rather than forcing the
          row wider. Below the breakpoint Site drops to its own full-width row
          (below) so the often-longer site name isn't squeezed against the
          download icon; Object + Detected + download stay on this row. */}
      <View flexDirection="row" gap="md" alignItems="flex-start">
        <View flex={1} style={{ minWidth: 0 }}>
          <Meta label="Object" value={dump.object_label} />
        </View>
        <View flex={1} style={{ minWidth: 0 }}>
          <Meta label="Detected" value={dump.detected_at_label} />
        </View>
        {siteInline ?
          <View flex={2} style={{ minWidth: 0 }}>
            <Meta label="Site" value={dump.site_name} />
          </View>
        : null}

        {playerClip ?
          <IconButton
            size="sm"
            title="Download clip"
            onPress={handleDownload}
            disabled={isDownloading}
            Icon={() => <Icon name="download" size={DOWNLOAD_ICON_SIZE} color={theme.colors.text} />}
          />
        : null}
      </View>

      {/* Second row (narrow only): Site spans the full width so its name has
          room instead of being crushed against the download icon on row 1. */}
      {!siteInline ?
        <View style={{ minWidth: 0 }}>
          <Meta label="Site" value={dump.site_name} />
        </View>
      : null}

      {/* Line: description spans the full width beneath the meta row(s). */}
      {dump.description !== "" ?
        <View gap="xxs">
          <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
            Description
          </Text>
          <Text textStyle="sm/regular" color="text">
            {dump.description}
          </Text>
        </View>
      : null}
    </View>
  );
}

/**
 * Thumbnail contents for one dump in the carousel strip. Uses the clip's
 * thumbnail image when resolved; falls back to a labelled placeholder while it
 * loads or if the clip is missing. The carousel supplies the pressable frame.
 */
function DumpThumbnail({ dump }: { dump: WasteSenseDumpDetection }) {
  const { playerClip } = useDumpClip(dump);
  const { theme } = useUnistyles();

  if (playerClip?.thumbnailUrl) {
    return (
      <img
        src={playerClip.thumbnailUrl}
        alt={`${dump.site_name} — ${dump.object_label}`}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    );
  }

  return (
    <View
      flex={1}
      alignItems="center"
      justifyContent="center"
      style={{ backgroundColor: theme.colors.borderCard as string }}
    >
      <Text textStyle="xxs/semibold" color="textMuted" numberOfLines={1}>
        {dump.object_label}
      </Text>
    </View>
  );
}

function Meta({ label, value }: MetaProps) {
  return (
    <View gap="xxs">
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      <Text textStyle="sm/semibold" color="text">
        {value}
      </Text>
    </View>
  );
}
